import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from '@/components/modal';
import { Button } from '@/components/button/Buttons';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';

export type CancelSubscriptionEntityType =
  | 'boater'
  | 'harbor'
  | 'club'
  | 'service-provider'
  | 'dmo';

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: CancelSubscriptionEntityType;
  entityId: string;
  entityName?: string;
  subscriptionEndDate?: string | null;
  onSuccess?: () => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName,
  subscriptionEndDate,
  onSuccess,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      let result: { message?: string; status?: boolean };
      switch (entityType) {
        case 'boater':
          result = await SubscriptionsService.cancelUserSubscriptionAutoRenew(entityId);
          break;
        case 'harbor':
          result = await SubscriptionsService.cancelHarborSubscriptionAutoRenew(entityId);
          break;
        case 'club':
          result = await SubscriptionsService.cancelClubSubscriptionAutoRenew(entityId);
          break;
        case 'service-provider':
          result = await SubscriptionsService.cancelServiceProviderSubscriptionAutoRenew(entityId);
          break;
        case 'dmo':
          result = await SubscriptionsService.cancelDmoSubscriptionAutoRenew(entityId);
          break;
        default:
          throw new Error('Unsupported entity type');
      }

      enqueueSnackbar(
        result?.message ||
          intl.formatMessage({ id: 'SUBSCRIPTION.CANCEL_PLAN.SUCCESS' }),
        { variant: result?.status === false ? 'warning' : 'success' }
      );
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      const e = error as { response?: { data?: { message?: string } } };
      enqueueSnackbar(
        e?.response?.data?.message ||
          intl.formatMessage({ id: 'SUBSCRIPTION.CANCEL_PLAN.FAILED' }),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const title = intl.formatMessage({ id: 'SUBSCRIPTION.CANCEL_PLAN.TITLE' });
  const message = subscriptionEndDate
    ? intl.formatMessage(
        { id: 'SUBSCRIPTION.CANCEL_PLAN.MESSAGE_WITH_DATE' },
        { name: entityName || '', date: subscriptionEndDate }
      )
    : intl.formatMessage(
        { id: 'SUBSCRIPTION.CANCEL_PLAN.MESSAGE' },
        { name: entityName || '' }
      );

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600 mb-6">{message}</p>
          <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={loading}>
              {intl.formatMessage({ id: 'COMMON.CANCEL' })}
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading
                ? intl.formatMessage({ id: 'SUBSCRIPTION.CANCEL_PLAN.PROCESSING' })
                : intl.formatMessage({ id: 'SUBSCRIPTION.CANCEL_PLAN.CONFIRM' })}
            </Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CancelSubscriptionModal;
